<template>
  <div
    class="wrapper"
    :class="[
      { 'nav-open': $sidebar.showSidebar },
      { rtl: $route.meta.rtlActive }
    ]"
  >
    <div
      v-if="hiddenMobileView"
      class="flex justify-center items-center w-full h-full text-center"
    >
      <h3 class="text-lg font-medium px-4">
        {{ $t("error.mobile_view") }}
        <div>
          <md-button class="md-warning mt-4" @click="logout">
            <i class="fas fa-sign-out-alt"></i> {{ $t("sidebar.logout") }}
          </md-button>
        </div>
      </h3>
    </div>
    <div v-else>
      <isis-timezone-modal v-if="!hasTimezone"></isis-timezone-modal>
      <notifications></notifications>
      <side-bar
        :active-color="sidebarBackground"
        :background-image="sidebarBackgroundImage"
        :data-background-color="sidebarBackgroundColor"
      >
        <user-menu
          :title="userName"
          :myProfile="myProfilePath"
          :avatar="avatarUrl"
        ></user-menu>
        <mobile-menu></mobile-menu>
        <template slot="links" v-if="userRole">
          <template v-for="(link, index) in sidebarItems">
            <sidebar-item :key="link.name + index" :link="link">
              <template v-for="(subLink, index) in link.children">
                <sidebar-item :key="subLink.name + index" :link="subLink">
                </sidebar-item>
              </template>
            </sidebar-item>
          </template>
        </template>
      </side-bar>
      <div class="main-panel">
        <top-navbar></top-navbar>
        <fixed-plugin v-if="shouldShowSettingIcon"></fixed-plugin>
        <div
          :class="{ content: !$route.meta.hideContent }"
          @click="toggleSidebar"
        >
          <zoom-center-transition :duration="200" mode="out-in">
            <!-- your content here -->
            <router-view></router-view>
          </zoom-center-transition>
        </div>
        <!-- <content-footer v-if="!$route.meta.hideFooter"></content-footer> -->
      </div>
    </div>
  </div>
</template>
<script>
/* eslint-disable no-new */
import PerfectScrollbar from "perfect-scrollbar";
import "perfect-scrollbar/css/perfect-scrollbar.css";

function hasElement(className) {
  return document.getElementsByClassName(className).length > 0;
}

function initScrollbar(className) {
  if (hasElement(className)) {
    new PerfectScrollbar(`.${className}`);
    document.getElementsByClassName(className)[0].scrollTop = 0;
  } else {
    // try to init it later in case this component is loaded async
    setTimeout(() => {
      initScrollbar(className);
    }, 100);
  }
}

function reinitScrollbar() {
  let docClasses = document.body.classList;
  let isWindows = navigator.platform.startsWith("Win");
  if (isWindows) {
    // if we are on windows OS we activate the perfectScrollbar function
    initScrollbar("sidebar");
    initScrollbar("sidebar-wrapper");
    initScrollbar("main-panel");

    docClasses.add("perfect-scrollbar-on");
    docClasses.add("is-window");
  } else {
    docClasses.add("perfect-scrollbar-off");
  }
}

import FixedPlugin from "@/pages/FixedPlugin.vue";
import MobileMenu from "../Extra/MobileMenu.vue";
import UserMenu from "../Extra/UserMenu.vue";
import TopNavbar from "../TopNavbar.vue";
import KeycloakLib from "@/libs/Keycloak";
import Role from "@/constants/Role";
import { ZoomCenterTransition } from "vue2-transitions";
import { Message, getId } from "@/helpers/utils";
import { hasValidRole } from "@/helpers/role-helper";
import { mapState, mapGetters } from "vuex";
import IsisTimezoneModal from "@/components/IsisTimezoneModal";

import {
  familyPages,
  dashboard_router_name,
  notification_names,
  family_router_names
} from "@/routes/routes";
import { SideBarItemCfgs } from "./sidebar-item-cfg";
import Swal from "sweetalert2";
import { removeDeviceToken } from "@/helpers/firebase";
import { store } from "@/store";

export default {
  components: {
    TopNavbar,
    FixedPlugin,
    MobileMenu,
    UserMenu,
    ZoomCenterTransition,
    IsisTimezoneModal
  },
  props: ["noTopBar"],
  data() {
    return {
      sidebarBackgroundColor: "black",
      sidebarBackground: "green",
      sidebarBackgroundImage: "./img/sidebar-2.jpg",
      sidebarMini: true,
      sidebarImg: true,
      hiddenMobileView: false
    };
  },
  computed: {
    ...mapState("user", ["info"]),
    ...mapState("switchAccountChild", ["currentChild"]),
    ...mapGetters("user", {
      userRole: "role"
    }),
    ...mapGetters("fcm", {
      storeNewNotification: "newNotification"
    }),
    userName() {
      return this.info.username;
    },
    myProfilePath() {
      const currentUserId = getId(this.info);
      if (hasValidRole(this.userRole, Role.Family)) {
        return `${familyPages.path}/my-profile`;
      }
      return `/my-profile/${currentUserId}`;
    },
    hasCurrentChildAccount() {
      return !!this.currentChild;
    },
    sidebarItems() {
      if (this.hasCurrentChildAccount) {
        return this.generateChildSidebar();
      }

      return this.generateGeneralSidebar();
    },
    hasTimezone() {
      return this.info?.reference?.timezone;
    },
    avatarUrl() {
      return this.info?.avatar_url || "./img/default-avatar.png";
    },
    shouldShowSettingIcon() {
      const listPageName = [
        dashboard_router_name,
        family_router_names.accountDetail,
        notification_names.listPage
      ];
      return (
        !hasValidRole(this.userRole, Role.Family) ||
        (hasValidRole(this.userRole, Role.Family) &&
          listPageName.includes(this.$route.name))
      );
    }
  },
  methods: {
    handleResize() {
      const widthView = window.innerWidth;
      this.hiddenMobileView =
        widthView < 1024 &&
        !hasValidRole(this.userRole, [Role.Family, Role.Student]);
    },
    logout() {
      Swal.fire({
        title: this.$t("common.are_you_sure"),
        text: this.$t("common.want_to_logout"),
        type: "warning",
        showCancelButton: true,
        confirmButtonClass: "md-button md-success btn-fill",
        cancelButtonClass: "md-button md-danger btn-fill",
        confirmButtonText: this.$t("common.yes"),
        buttonsStyling: false
      }).then(result => {
        if (result.value) {
          removeDeviceToken();
          KeycloakLib.doLogout();
          store.dispatch("schoolAuth/Logout");
        }
      });
    },
    toggleSidebar() {
      if (this.$sidebar.showSidebar) {
        this.$sidebar.displaySidebar(false);
      }
    },
    minimizeSidebar() {
      if (this.$sidebar) {
        this.$sidebar.toggleMinimize();
      }
    },
    generateChildSidebar() {
      return [
        {
          name: this.currentChild.full_name,
          icon: "face",
          children: this.generateGeneralSidebar({
            childUser: this.currentChild
          })
        }
      ];
    },
    generateGeneralSidebar(opt) {
      const sidebar = SideBarItemCfgs.filter(itemCfg =>
        itemCfg.hasPermission(this.userRole)
      ).map(itemCfg => {
        const { hasPermission, generatePath, name, ...resCfg } = itemCfg;

        return {
          ...resCfg,
          name: this.$t(name),
          path: generatePath({
            ...opt,
            rootUser: this.info,
            rootUserRole: this.userRole
          })
        };
      });
      const sidebarMultiLevel = [];
      sidebar.forEach(item => {
        if (!item.group && !item.groupName) sidebarMultiLevel.push(item);

        if (item.group) {
          let child = sidebar.filter(el => el.groupName === item.group);
          if (child) item.children = child;
          sidebarMultiLevel.push(item);
        }
      });
      return sidebarMultiLevel;
    }
  },
  created() {
    this.handleResize();
    window.addEventListener("resize", this.handleResize);
  },
  updated() {
    reinitScrollbar();
  },
  mounted() {
    reinitScrollbar();
  },
  destroyed() {
    window.removeEventListener("resize", this.handleResize);
  },
  watch: {
    sidebarMini() {
      this.minimizeSidebar();
    },
    storeNewNotification: {
      handler: function(newVal) {
        Message.success(
          newVal.notification.title + ": " + newVal.notification.body
        );
      }
    }
  }
};
</script>

<!-- CSS Global -->
<style lang="scss">
$scaleSize: 0.95;
@keyframes zoomIn95 {
  from {
    opacity: 0;
    transform: scale3d($scaleSize, $scaleSize, $scaleSize);
  }
  to {
    opacity: 1;
  }
}

.main-panel .zoomIn {
  animation-name: zoomIn95;
}

@keyframes zoomOut95 {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
    transform: scale3d($scaleSize, $scaleSize, $scaleSize);
  }
}

.main-panel .zoomOut {
  animation-name: zoomOut95;
}

/* Hide scrollbar for Chrome, Safari and Opera */
.is-window.perfect-scrollbar-on::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.is-window.perfect-scrollbar-on {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}

.list-page-header {
  display: flex;
  align-items: center;
  padding-left: 15px;

  h3 {
    width: fit-content;
    margin: 0;

    font-size: 1.5rem;
    font-weight: 500;
  }

  .primary-bulk-actions {
    margin-left: 25px;
  }
}

.template-desc {
  margin: 0;
  font-size: 14px;

  a {
    font-weight: 500;
  }
}

.word-break-title {
  word-break: break-word;
  white-space: pre-line;
}
</style>
<style lang="scss" scoped>
.fixed-plugin:not(.change-pwd) {
  top: 110px !important;
}
</style>
